import axios from "axios";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { defaultUrl } from "../../features/actions";
import { setSharedState } from "../../features/sharedStateSlices/shareState";
import { parentFromAPI } from "../../types/interfaces";
import { Lock, UserMinus, ShieldAlert, Mail, Trash2 } from "lucide-react";

const PRIMARY = "#0F2A6B";

interface EmbeddedParentFormProps {
  numberOfParents?: number;
  form?: parentFromAPI[];
  studentEdittingSaved?: boolean;
  handleParentChange?: (
    index: number,
    e: any,
    updatedParents?: parentFromAPI[]
  ) => void;
  studentId: string;
  onParentUnlinked?: () => void;
}

export const EmbeddedParentForm = ({
  numberOfParents = 0,
  form,
  studentEdittingSaved,
  handleParentChange = () => { },
  studentId,
  onParentUnlinked,
}: EmbeddedParentFormProps) => {
  const dispatch = useDispatch();
  const [localParents, setLocalParents] = useState<parentFromAPI[]>(form || []);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const token = sessionStorage.getItem("token")?.replace(/['"]+/g, "");

  useEffect(() => {
    setLocalParents(form || []);
  }, [form]);

  /* ================= DELETE / UNLINK ================= */

  const unlinkParent = async (parentId: string, parentName?: string) => {
    const confirmResult = await Swal.fire({
      title: "Unlink Parent Connection?",
      text: `Are you sure you want to unlink ${
        parentName ? `"${parentName}"` : "this parent"
      } from this child? The parent will no longer have access to this pupil's records.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Unlink Parent",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        const headers = {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        };
        try {
          await axios.put(
            `${defaultUrl}/parents/children/remove`,
            {
              data: [
                {
                  parent_id: parentId,
                  children: [studentId],
                },
              ],
            },
            { headers }
          );
          return true;
        } catch (error) {
          Swal.showValidationMessage("Failed to unlink parent");
          return false;
        }
      },
      customClass: {
        popup: "rounded-2xl shadow-xl border border-slate-100",
        confirmButton: "rounded-xl font-semibold px-4 py-2 text-xs",
        cancelButton: "rounded-xl font-semibold px-4 py-2 text-xs",
      },
    });

    if (confirmResult.isConfirmed) {
      toast.success("Parent unlinked successfully");
      const updatedParents = localParents.filter((parent) => parent.id !== parentId);
      setLocalParents(updatedParents);
      dispatch(setSharedState());
      if (onParentUnlinked) {
        onParentUnlinked();
      }
    }
  };

  const deleteParentPermanently = async (parentId: string, parentName?: string) => {
    const confirmResult = await Swal.fire({
      title: "Delete Parent Permanently?",
      text: `Are you sure you want to permanently delete ${
        parentName ? `"${parentName}"` : "this parent"
      } from the system? The parent will no longer have access to any of the linked children.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete Parent",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        const headers = {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        };
        try {
          try {
            await axios.delete(`${defaultUrl}/parents/delete/${parentId}`, { headers });
          } catch {
            await axios.delete(`${defaultUrl}/parents/${parentId}`, { headers });
          }
          return true;
        } catch (error) {
          Swal.showValidationMessage("Failed to delete parent from system");
          return false;
        }
      },
      customClass: {
        popup: "rounded-2xl shadow-xl border border-slate-100",
        confirmButton: "rounded-xl font-semibold px-4 py-2 text-xs",
        cancelButton: "rounded-xl font-semibold px-4 py-2 text-xs",
      },
    });

    if (confirmResult.isConfirmed) {
      toast.success("Parent deleted successfully from the system");
      const updatedParents = localParents.filter((parent) => parent.id !== parentId);
      setLocalParents(updatedParents);
      dispatch(setSharedState());
      if (onParentUnlinked) {
        onParentUnlinked();
      }
    }
  };

  /* ================= FIELD CHANGES ================= */

  const handleInputChange = (idx: number, e: any) => {
    const { name, value } = e.target;
    setLocalParents((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [name]: value };
      if (handleParentChange) {
        handleParentChange(idx, e, updated);
      }
      return updated;
    });
  };

  const handleRelationshipChange = (idx: number, relValue: string) => {
    setLocalParents((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], relationship: relValue };
      const event = { target: { name: "relationship", value: relValue } };
      if (handleParentChange) {
        handleParentChange(idx, event, updated);
      }
      return updated;
    });
  };

  /* ================= EMPTY STATE ================= */
  if (!localParents || localParents.length === 0) {
    return (
      <div className="bg-slate-50/80 border-2 border-dashed border-slate-200 rounded-2xl p-6 text-center flex flex-col items-center justify-center space-y-3">
        <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-200/60 shadow-sm">
          <Mail className="w-6 h-6" />
        </div>
        <div>
          <h4 className="text-sm font-bold text-slate-800 tracking-tight">
            No Parent Email Attached
          </h4>
          <p className="text-xs text-slate-500 max-w-sm mt-1 leading-relaxed">
            This pupil currently has no parent or guardian email linked. Notifications and invoice alerts will not be delivered to a parent.
          </p>
        </div>
      </div>
    );
  }

  /* ================= UI ================= */

  return (
    <div className="space-y-5">
      {localParents.map((parent: parentFromAPI, idx: number) => (
        <div
          key={parent.id || idx}
          className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm transition hover:shadow-md"
        >
          {/* HEADER */}
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-900 font-bold text-xs flex items-center justify-center border border-blue-100">
                {idx + 1}
              </span>
              <h3 className="text-sm font-bold text-slate-800">
                Parent / Guardian Details
              </h3>
            </div>
            {parent.email && (
              <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-md border border-slate-200/60 flex items-center gap-1">
                <Lock size={10} className="text-slate-400" />
                {parent.email}
              </span>
            )}
          </div>

          {/* GRID */}
          <div className="grid grid-cols-2 gap-3.5">
            {/* NAME */}
            <div>
              <label className="label">Full Name</label>
              <input
                type="text"
                name="name"
                value={parent?.name || ""}
                onChange={(e) => handleInputChange(idx, e)}
                required
                className="input"
              />
            </div>

            {/* EMAIL (NON-EDITABLE / DISABLED) */}
            <div>
              <div className="flex items-center justify-between">
                <label className="label flex items-center gap-1">
                  <span>Email Address</span>
                  <Lock size={10} className="text-amber-600" />
                </label>
                <span className="text-[10px] text-amber-700 font-semibold">
                  Read-Only
                </span>
              </div>
              <input
                type="email"
                name="email"
                value={parent?.email || ""}
                readOnly
                disabled
                className="input !bg-slate-100/90 !text-slate-500 !cursor-not-allowed border-slate-200 select-none"
                title="Parent email is non-editable to preserve account link integrity"
              />
            </div>

            {/* PHONE */}
            <div>
              <label className="label">Mobile Number</label>
              <input
                type="tel"
                name="phone"
                value={parent?.phone || ""}
                onChange={(e) => handleInputChange(idx, e)}
                required
                className="input"
              />
            </div>

            {/* HOME */}
            <div>
              <label className="label">Home Number</label>
              <input
                type="tel"
                name="home_number"
                value={parent?.home_number || ""}
                onChange={(e) => handleInputChange(idx, e)}
                className="input"
              />
            </div>

            {/* WORK */}
            <div>
              <label className="label">Work Number</label>
              <input
                type="tel"
                name="work_number"
                value={parent?.work_number || ""}
                onChange={(e) => handleInputChange(idx, e)}
                className="input"
              />
            </div>

            {/* RELATIONSHIP */}
            <div>
              <label className="label">Relationship</label>
              <div className="flex gap-2 mt-1">
                {["Parent", "Guardian"].map((r) => (
                  <button
                    type="button"
                    key={r}
                    onClick={() => handleRelationshipChange(idx, r)}
                    className="flex-1 py-2 rounded-lg text-xs font-semibold border transition-all"
                    style={{
                      backgroundColor:
                        parent.relationship === r ? PRIMARY : "white",
                      color: parent.relationship === r ? "white" : "#475569",
                      borderColor:
                        parent.relationship === r ? PRIMARY : "#e2e8f0",
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* DEDICATED PARENT ACTION BAR */}
          <div className="mt-5 pt-3.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/70 -mx-5 -mb-5 px-5 py-3 rounded-b-2xl">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <ShieldAlert size={14} className="text-slate-400" />
              <span>Parent Actions</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => unlinkParent(parent.id, parent.name)}
                disabled={isDeleting === parent.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-amber-700 border border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-300 transition-all shadow-sm active:scale-95 disabled:opacity-50"
              >
                <UserMinus size={14} />
                {isDeleting === parent.id ? "Unlinking..." : "Unlink Parent"}
              </button>

              <button
                type="button"
                onClick={() => deleteParentPermanently(parent.id, parent.name)}
                disabled={isDeleting === parent.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white bg-red-600 hover:bg-red-700 border border-red-600 transition-all shadow-sm active:scale-95 disabled:opacity-50"
              >
                <Trash2 size={14} />
                {isDeleting === parent.id ? "Deleting..." : "Delete Parent"}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ================= SHARED STYLES ================= */

const styles = `
.input {
  width: 100%;
  margin-top: 4px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  outline: none;
  transition: all 0.2s;
}
.input:focus {
  border-color: #0F2A6B;
  box-shadow: 0 0 0 2px rgba(15,42,107,0.1);
}

.label {
  font-size: 11px;
  color: #6b7280;
}
`;

if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.innerHTML = styles;
  document.head.appendChild(style);
}

